import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import DataGrid, { Column, Lookup, Scrolling, Editing } from 'devextreme-react/data-grid';
import { CustomRule } from 'devextreme-react/validator';
import { Tooltip } from 'devextreme-react/tooltip';
import AntLocalization from '../../utils/AntLocalization';

export default forwardRef(function (props, ref) {
    const mainFields = props.importFields && props.importFields instanceof Array ? props.importFields : [];

    const [mappingRows, setMappingRows] = useState([]);
    const [excelColumns, setExcelColumns] = useState([]);

    const isRequiredField = (fieldName) => {
        var obj = mainFields.find((e) => e.name === fieldName);
        if (obj && obj.required === true) return true;

        return false;
    };

    const canCreateField = (fieldName) => {
        var obj = mainFields.find((e) => e.name === fieldName && e.create !== false);
        if (obj) return true;

        return false;
    };

    const canUpdateField = (fieldName) => {
        var obj = mainFields.find((e) => e.name === fieldName && e.update !== false);
        if (obj) return true;

        return false;
    };

    const load = () => {
        // aiColumnLabels: { dataField: label_obj }  — BE worker trả tên cột thân thiện
        // (đa ngôn ngữ) cho từng dataField. Display dùng label, matching internal
        // (lookup theo aiSuggestedMapping) vẫn dùng `column_name` = dataField.
        var aiLabels = (props.aiColumnLabels && typeof props.aiColumnLabels === 'object') ? props.aiColumnLabels : null;
        var xlsxCols = [];
        for (let i = 0; i < props.fileColumns.length; i++) {
            var raw = props.fileColumns[i];
            var displayName = raw;
            if (aiLabels && aiLabels[raw]) {
                var translated = AntLocalization.translateText(aiLabels[raw]);
                if (translated) displayName = translated;
            }
            xlsxCols.push({
                column_index: i,
                column_name: displayName,
                column_data_field: raw  // dùng cho matching aiSuggestedMapping
            });
        }

        // AI-driven mapping (BE worker → output_data.suggested_mapping). Khi AI extract,
        // headers là AI schema keys (`price`, `catalogue`, `sku`...) khác với Item dataField
        // (`default_unit_price`, `catalogue1`, `code`). Suggested_mapping nói rõ AI key nào
        // tương ứng với import_field nào → ưu tiên dùng nó trước khi fallback name-match.
        var aiMap = {};
        if (props.aiSuggestedMapping && props.aiSuggestedMapping instanceof Array) {
            props.aiSuggestedMapping.forEach((m) => {
                if (m && m.import_field && m.ai_field) aiMap[m.import_field] = m.ai_field;
            });
        }
        const findColByName = (colName) => {
            if (!colName) return -1;
            var target = String(colName).toLowerCase().trim();
            for (let k = 0; k < xlsxCols.length; k++) {
                // Ưu tiên match qua column_data_field (raw dataField — bất biến sang label),
                // fallback qua column_name display.
                var df = xlsxCols[k].column_data_field;
                if (df && String(df).toLowerCase().trim() === target) return k;
                if (String(xlsxCols[k].column_name).toLowerCase().trim() === target) return k;
            }
            return -1;
        };

        var rows = [];

        for (let i = 0; i < mainFields.length; i++) {
            var rLabel = AntLocalization.translateText(mainFields[i].label);
            var row = {
                field_name: mainFields[i].name,
                field_label: rLabel,
                required: isRequiredField(mainFields[i].name),
                canCreate: canCreateField(mainFields[i].name),
                canUpdate: canUpdateField(mainFields[i].name),
                alignment: mainFields[i].alignment,
                width: mainFields[i].width,
                fixed: mainFields[i].fixed,
                preserveEmptyOnUpdate: mainFields[i].preserveEmptyOnUpdate === true,
                clearEmptyField: mainFields[i].clearEmptyField
            };

            if (props.importMode === 'Create' && !row.canCreate) continue;
            if (props.importMode === 'Update' && !row.canUpdate) continue;
            if (props.importMode === 'CreateUpdate' && !row.canCreate && !row.canUpdate) continue;

            var matched = false;
            var aiKey = aiMap[mainFields[i].name];
            if (aiKey) {
                var aiIdx = findColByName(aiKey);
                if (aiIdx >= 0) {
                    row.xlsx_col_index = xlsxCols[aiIdx].column_index;
                    row.xlsx_col_name = xlsxCols[aiIdx].column_name;
                    matched = true;
                }
            }

            if (!matched) {
                for (let j = 0; j < xlsxCols.length; j++) {
                    if (
                        String(xlsxCols[j].column_name).trim() == String(rLabel).trim() ||
                        String(xlsxCols[j].column_name).toLowerCase() == String(rLabel).toLowerCase() ||
                        xlsxCols[j].column_name == mainFields[i].name ||
                        String(xlsxCols[j].column_name).toLowerCase().split('_').join(' ') ==
                            String(mainFields[i].name).toLowerCase().split('_').join(' ')
                    ) {
                        if (
                            String(xlsxCols[j].column_name).trim() == String(rLabel).trim() ||
                            String(xlsxCols[j].column_name).toLowerCase() == String(rLabel).toLowerCase()
                        )
                            row.xlsx_col_name = rLabel;
                        else row.xlsx_col_name = mainFields[i].name;

                        row.xlsx_col_index = xlsxCols[j].column_index;
                        matched = true;
                        break;
                    }
                }
            }

            // Skip dòng HOÀN TOÀN trống: FE config thiếu `label` cho field (vd
            // item_short_code, item_discount_amt chưa khai trong config/fields)
            // KHIẾN cột "Trường" hiển thị rỗng — và đồng thời file cũng không
            // có cột map → render row trống vô nghĩa. Ẩn để UI gọn.
            var hasLabel = rLabel && String(rLabel).trim() !== '';
            if (!hasLabel && row.xlsx_col_index === undefined) {
                continue;
            }

            rows.push(row);
        }

        xlsxCols.unshift({
            column_name: '',
            column_index: -1000
        });

        setMappingRows(rows);
        setExcelColumns(xlsxCols);
    };

    const validate = () => {
        var invalids = [];
        for (var i = 0; i < mappingRows.length; i++) {
            const item = mappingRows[i];

            if (item.required && (item.xlsx_col_index === undefined || item.xlsx_col_index < 0)) invalids.push(item.field_label);
        }
        return {
            isValid: invalids.length == 0,
            message: invalids.length ? `${AntLocalization.translateText({ vi: 'Phải chọn', en: 'Must select', fr: 'Sélectionnez', es: 'Seleccione', id: 'Wajib pilih', ja: '選択必須:', zh: '必须选择', ko: '선택 필수:', hi: 'चुनना ज़रूरी', ar: 'يجب اختيار' })} ${invalids.join(',')} !` : ''
        };
    };

    const getMappedFields = () => {
        return mappingRows;
    };

    const onRowUpdated = (e) => {
        setMappingRows((rows) => {
            let row = rows.find((r) => r.field_name === e.field_name);
            if (row) row.xlsx_col_index = e.xlsx_col_index;
            return rows;
        });
        if (props.onMapped) props.onMapped(mappingRows);
    };

    const fieldValidation = async (params) => {
        var row = params.data;
        var isValid = !row.required || params.value >= 0;
        if (!isValid) return false;
        return true;
    };

    const renderMappingCell = (info) => {
        if (!info.data.required || String(info.text).trim() != '') return <div>{info.text}</div>;

        var id = 'ant-error-' + info.data.field_name;

        return (
            <div>
                <i className="ant-grid-icon ant-icon-error dx-icon-info" id={id}></i>
                <Tooltip
                    target={`#${id}`}
                    className="ant-tooltip"
                    hideEvent="mouseleave"
                    showEvent={{
                        name: 'mouseenter dxclick keyup'
                    }}
                    hideOnOutsideClick={true}
                >
                    <div>{AntLocalization.translateText({ vi: 'Bắt buộc chọn', en: 'Must select', fr: 'Must select', es: 'Must select', id: 'Pilihan wajib', ja: '選択必須', zh: '必须选择', ko: '선택 필수', hi: 'चयन आवश्यक', ar: 'الاختيار مطلوب' })}</div>
                </Tooltip>
            </div>
        );
    };

    useEffect(() => {
        if (props.fileColumns && props.fileColumns.length > 0) load();
    }, [props.fileColumns, props.aiSuggestedMapping]);

    useEffect(() => {
        if (props.onMapping) props.onMapping(mappingRows);
    }, [mappingRows]);

    useImperativeHandle(ref, () => ({
        validate: validate,
        getMappedFields: getMappedFields
    }));

    return (
        <React.Fragment>
            <DataGrid
                noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
                className={'dx-card wide-card'}
                dataSource={mappingRows}
                keyExpr="field_name"
                onRowUpdated={onRowUpdated}
                focusedRowEnabled={false}
                columnAutoWidth={true}
                columnHidingEnabled={false}
                showEditorAlways={true}
                showColumnLines={false}
                showRowLines={true}
                showBorders={true}
                rowAlternationEnabled={true}
                visible={props.visible}
                height={'100%'}
                // style={{ flex: 1 }}
            >
                <Editing mode="cell" allowUpdating={true} />

                <Scrolling mode="infinite" />
                <Column caption={' '} alignment={'left'} width={30} allowEditing={false} />
                <Column
                    dataField={'field_label'}
                    caption={AntLocalization.translateText({ vi: 'Trường', en: 'Field', fr: 'Champ', es: 'Campo', id: 'Kolom', ja: '項目', zh: '字段', ko: '필드', hi: 'फ़ील्ड', ar: 'الحقل' })}
                    allowEditing={false}
                    alignment={'left'}
                />

                <Column
                    dataField="xlsx_col_index"
                    caption={AntLocalization.translateText({ vi: 'Cột trong file', en: 'Column in file', fr: 'Colonne fichier', es: 'Columna archivo', id: 'Kolom di file', ja: 'ファイルの列', zh: '文件中的列', ko: '파일의 열', hi: 'फ़ाइल का कॉलम', ar: 'عمود الملف' })}
                    alignment={'left'}
                    placeholder={AntLocalization.translateText({ vi: 'Chọn cột tương ứng', en: 'Select matching column', fr: 'Choisir la colonne', es: 'Elegir la columna', id: 'Pilih kolom yang sesuai', ja: '対応する列を選択', zh: '选择对应的列', ko: '해당 열 선택', hi: 'संबंधित कॉलम चुनें', ar: 'اختر العمود المطابق' })}
                    cellRender={renderMappingCell}
                    editorOptions={{
                        showClearButton: true
                    }}
                >
                    <Lookup dataSource={excelColumns} displayExpr="column_name" valueExpr="column_index" />
                    <CustomRule
                        type="async"
                        reevaluate={true}
                        validationCallback={fieldValidation}
                        message={AntLocalization.translateText({ vi: 'Bắt buộc chọn"', en: 'Must Select', fr: 'Sélection requise', es: 'Obligatorio', id: 'Wajib dipilih', ja: '選択必須', zh: '必选', ko: '선택 필수', hi: 'चुनना ज़रूरी', ar: 'الاختيار مطلوب' })}
                    />
                </Column>
            </DataGrid>
        </React.Fragment>
    );
});
